import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Popup } from "@progress/kendo-react-popup";
import { Tooltip } from "@progress/kendo-react-tooltip";
import useOnClickOutside from "use-onclickoutside";
import { setActionStatus, setEditEntity } from "../../../../actions/comments";
import { button, icon, number, tooltip } from "../../../../config";
import { replyBubbleConfig } from "../../../../config/constants";
import { appendFieldsFromObject, createFormData } from "../../../../helper/formDataHelper";
import { useReminders } from "../../../../shared/layouts/Header/MainHeader/remindersHook";
import { onBlur, onFocus, onOpen } from "../../../../utils/kendo";
import DynamicReminderPopup from "../../../Tasks/Reminders/DynamicReminderPopup";
import { ReminderContextProvider } from "../../../Tasks/Reminders/ReminderContextProvider";
import { useDynamicReminderPopup } from "../../../Tasks/Reminders/taskRemindersHook";
import ReminderMenu from "./ReminderMenu";
import { commentActionStatus } from "./comments.constants";
import { deleteComment, deleteReply } from "./comments.service";
import { useCommentActions } from "./hooks/newComments.hooks";

/**
 * PC : Comments || CommentReply
 * This renders options menu for comments
 * @author Sarthak Arora
 */
const CommentsDropdown = (props) => {
  const { taskId, comment, reply, commentId, replyId, moveFocusToEditor, moveTaskSidebarScrollToEditor, type, setContent, setReplyBubble, isNewTask, isOwnComment } = props;

  const [showRemindMenu, setShowRemindMenu] = useState(false);
  const [showCustomPopup, setShowCustomPopup] = useState(false);
  const [isClickingInside, setIsClickingInside] = useState(false);

  const userId = useSelector((state) => state.auth.user.id);
  const companyId = useSelector((state) => state.auth.user.companyId);
  const { allComments } = useSelector((state) => state.comments);
  const { deleteCommentforNewTask } = useCommentActions();
  const doc = new DOMParser().parseFromString(comment?.CommentText, "text/html");
  const commentText = doc.body.textContent;
  const { popupBodyContent, handleSaveReminder, handleCancel } = useDynamicReminderPopup({ setShowReminder: setShowCustomPopup, reminderType: number.FIVE, commentText: commentText });
  const anchorAlign = { horizontal: "left", vertical: "top" };
  const popupAlign = { horizontal: "right", vertical: "middle" };
  const [openDuration, setOpenDuration] = React.useState(300);
  const [closeDuration, setCloseDuration] = React.useState(300);

  const [showDrop, setShowDrop] = useState(false);
  const dropdownRef = useRef(null);
  useOnClickOutside(dropdownRef, () => {
    if (!isClickingInside) {
      setShowDrop(false);
    }
  });

  const deleteRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  const actionButtonRef = useRef(null);
  const remindMeRef = useRef(null);

  const isDeleted = number.ONE;
  const dispatch = useDispatch();

  const hideOnBlur = () => {
    if (!isClickingInside) {
      setShowRemindMenu(false);
    }
  };
  /**
   * handling  comments & reply  edit
   * @param {void}
   * @returns {void}
   */
  const handleEdit = () => {
    moveFocusToEditor();
    moveTaskSidebarScrollToEditor();
    setReplyBubble(replyBubbleConfig.NONE);
    if (type === "comment") {
      dispatch(setActionStatus(commentActionStatus?.EDIT_COMMENT));
      dispatch(setEditEntity(comment));
    } else {
      dispatch(setActionStatus(commentActionStatus?.EDIT_REPLY));
      dispatch(setEditEntity(reply));
    }
    setShowDrop(!showDrop);
  };

  /**
   * handling  comments & reply delete
   * @param {void}
   * @returns {void}
   */
  const handleDelete = async () => {
    setShowDrop(!showDrop);
    setReplyBubble(replyBubbleConfig.NONE);
    if (type === "comment") {
      const payload = { isDeleted, commentId, images: comment?.CommentImages };
      const formData = createFormData();
      appendFieldsFromObject(formData, payload);
      isNewTask ? deleteCommentforNewTask(comment) : await dispatch(deleteComment(formData));
      setContent("");
    } else {
      const payload = { isDeleted, replyId, taskId, userId, limit: allComments.length, companyId, images: reply.ReplyImages };
      const formData = createFormData();
      appendFieldsFromObject(formData, payload);
      dispatch(deleteReply(formData, commentId));
    }
  };

  /**
   * when the popup is clicked the we set seIsClicking Inside to true
   * @param {*} reminderType
   * @author Sarthak Arora
   */
  const handleMouseDown = () => {
    setIsClickingInside(true);
  };

  /**
   * when the popup is clicked outside  then we set seIsClicking Inside to false
   * @param {*} reminderType
   * @author Sarthak Arora
   */
  const handleMouseUp = () => {
    setTimeout(() => {
      setIsClickingInside(false);
    }, 1000);
  };

  return (
    <>
      <div className='dropdown' ref={dropdownRef}>
        <button
          className='dropbtn'
          ref={actionButtonRef}
          type='button'
          title={tooltip.ACTIONS}
          onClick={() => {
            setShowDrop(!showDrop);
          }}>
          <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
            {icon.TRIPLE_DOT}
          </Tooltip>
        </button>
      </div>
      <Popup
        anchor={actionButtonRef.current}
        appendTo={actionButtonRef.current}
        show={showDrop}
        onOpen={() => onOpen(deleteRef)}
        popupClass={"dt-popup"}
        popupAlign={popupAlign}
        anchorAlign={anchorAlign}
        collision={{
          horizontal: "fit",
          vertical: "fit",
        }}
        animate={{
          openDuration,
          closeDuration,
        }}>
        <div ref={deleteRef} tabIndex={number.ZERO} onFocus={() => onFocus(blurTimeoutRef)} onBlur={() => onBlur(blurTimeoutRef, hideOnBlur)} onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
          <div className={showDrop ? "dt-popup-body comment-actions dropdown-content reply-actions show" : "dropdown-content"}>
            {isOwnComment && (
              <span className='cursor-pointer reminder-menu-item' onClick={handleEdit}>
                {button.COMMENT_EDIT}
              </span>
            )}

            {isOwnComment && (
              <span className='py-1  cursor-pointer reminder-menu-item' onClick={handleDelete}>
                {button.COMMENT_DELETE}
              </span>
            )}

            <span
              className='cursor-pointer relative reminder-menu-item'
              ref={remindMeRef}
              onClick={(e) => {
                e.preventDefault();
                setShowRemindMenu(!showRemindMenu);
              }}>
              {button.REMIND_ME}
            </span>
          </div>
        </div>
      </Popup>

      {showRemindMenu && (
        <ReminderContextProvider>
          <ReminderMenu
            setShowDrop={setShowDrop}
            setShowRemindMenu={setShowRemindMenu}
            setShowCustomPopup={setShowCustomPopup}
            anchor={remindMeRef}
            comment={commentText}
            handleMouseDown={handleMouseDown}
            handleMouseUp={handleMouseUp}
          />
        </ReminderContextProvider>
      )}

      {showCustomPopup && (
        <DynamicReminderPopup
          show={showCustomPopup}
          setShowReminder={setShowCustomPopup}
          popupBodyContent={popupBodyContent}
          popupAnchor={dropdownRef}
          handleSaveReminder={handleSaveReminder}
          handleCancel={handleCancel}
          extraWidth={true}
          animate={true}
          skipButtons={true}
          anchorAlign={anchorAlign}
          popupAlign={popupAlign}
        />
      )}
    </>
  );
};

export default CommentsDropdown;
